import React from 'react';
import { AssignmentCard } from '../../../components/assignmentCard';
import { Assignment, UpNext } from '../../training';
import { SmallPrimaryButton } from '../../../components/Buttons';
import AssignmentCardDownloads from './AssignmentCardDownloads';
import StepIcons from '../../../components/StepIcons';
import windowService from '../../../services/windowService';
import { purchaseLockedItem } from '../../../utils/purchaseUtils';
import alertService from '../../../services/AlertService';

type DashboardAssignmentCardProps = {
  assignment: Assignment;
  isActiveAssignment: boolean;
  assigneeUserId?: string;
  actionButtons?: React.JSX.Element | null;
};

const redirectToTask = (assignment: Assignment, isPathway: boolean, upNext?: UpNext) => {
  localStorage.setItem('routeBeforeSteps', window.location.hash);

  if (isPathway) {
    windowService.redirectTo(
      '#/training-steps/' + assignment.instanceId + `?stepId=${upNext?.instanceId}&isWorkflow=true`
    );
  } else {
    windowService.redirectTo('#/training-steps/' + assignment.instanceId + `?stepId=${upNext?.instanceId}`);
  }
};

export function DashboardAssignmentCard({
  assignment,
  isActiveAssignment,
  assigneeUserId,
  actionButtons,
}: DashboardAssignmentCardProps) {
  const isActivePathway = assignment._type === 'ActiveWorkflowOverview';
  const isCompletePathway = assignment._type === 'CompleteWorkflowOverview';
  const isPathway = isActivePathway || isCompletePathway;
  const isLocked = 'purchaseRequired' in assignment && assignment.purchaseRequired;

  const training = isActivePathway ? assignment.activeCourse : assignment;
  const upNext = 'upNext' in training ? training.upNext : undefined;
  const trainingProgress = training?.progress.percent;

  const completedCourses = assignment.progress.steps.completed;
  const totalCourses = assignment.progress.steps.total;

  const steps = training?.progress.steps;
  const stepLabel = isActiveAssignment ? 'step' : isCompletePathway ? 'step' : 'course';

  const handleRedirectToTask = () => redirectToTask(assignment, isPathway, upNext);

  const handlePurchase = () => {
    const req = 'purchaseRequired' in assignment ? assignment.purchaseRequired : undefined;
    const value = typeof req === 'object' ? req?.value : req;
    if (value && typeof value === 'string') {
      if (!purchaseLockedItem(value)) {
        alertService.showError();
      }
    }
  };

  return (
    <AssignmentCard data-qa-hook="assignmentCard">
      {isActivePathway && (
        <AssignmentCard.Header
          currentCourse={completedCourses + 1}
          totalCourses={totalCourses}
          pathwayName={assignment.name}
        />
      )}
      <AssignmentCard.Body>
        <AssignmentCard.Poster src={assignment.posterImage} fallbackName={assignment.name} />
        <AssignmentCard.Content>
          <AssignmentCard.Title name={training?.name ?? ''} onClick={handleRedirectToTask} />
          <AssignmentCard.Metadata
            type={isPathway ? 'pathway' : 'course'}
            stepsCompleted={steps?.completed}
            stepsTotal={steps?.total}
            stepLabel={stepLabel}
            completedOn={'completedOn' in assignment ? assignment.completedOn : undefined}
            dueDate={assignment.dueDate}
          />
          {isCompletePathway ? (
            <AssignmentCard.Progress variant="pathway" segments={assignment.completedCourses?.map(() => 100) ?? []} />
          ) : (
            <AssignmentCard.Progress variant="course" segments={[trainingProgress ?? 0]} />
          )}
          <AssignmentCardDownloads assignment={assignment} assigneeUserId={assigneeUserId} />
        </AssignmentCard.Content>
        {actionButtons && <AssignmentCard.Actions>{actionButtons}</AssignmentCard.Actions>}
      </AssignmentCard.Body>
      {(isLocked || upNext) && (
        <AssignmentCard.Footer>
          {isLocked ? (
            <>
              <div />
              <div>
                <SmallPrimaryButton onClick={handlePurchase} aria-label="purchase to unlock">
                  Purchase to Unlock
                </SmallPrimaryButton>
              </div>
            </>
          ) : (
            upNext && (
              <AssignmentCard.UpNext
                name={upNext.name}
                iconClass={StepIcons[upNext._type as keyof typeof StepIcons]}
                onClick={handleRedirectToTask}
              />
            )
          )}
        </AssignmentCard.Footer>
      )}
    </AssignmentCard>
  );
}
